import { TrendingDown, TrendingUp } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Card, CardContent } from "@/shared/ui/card";

import type { GeoReport } from "../../../api/geoService";
import { formatScoreDelta, roundDelta } from "../../../lib/scoreDelta";

function gradeColor(grade: string): string {
  if (grade === "优") return "bg-emerald-500";
  if (grade === "良") return "bg-blue-500";
  if (grade === "一般") return "bg-amber-500";
  return "bg-rose-500";
}

/** 全探针口径三率:提及率 / 明确推荐率 / Top1 率(疑虑阶段照评分口径剔除)。 */
function ProbeStats({ report }: { report: GeoReport }) {
  const probes = (report.probes ?? []).filter((p) => p.stage !== "doubt");
  if (probes.length === 0) return null;
  const pct = (n: number) => `${Math.round((n / probes.length) * 100)}%`;
  const mentioned = probes.filter((p) => p.mentioned).length;
  const recommended = probes.filter((p) => p.is_recommended).length;
  const top1 = probes.filter((p) => p.rank === 1).length;
  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="geo-probe-stats">
      <span>
        提及率 <b className="tabular-nums">{pct(mentioned)}</b>
      </span>
      <span>
        明确推荐率 <b className="tabular-nums">{pct(recommended)}</b>
      </span>
      <span>
        Top1 率 <b className="tabular-nums">{pct(top1)}</b>
      </span>
    </div>
  );
}

/** 总分卡:大数字 + 等级 + 较上轮 delta(有历史时)。 */
export function ScoreHero({ report, prevScore }: { report: GeoReport; prevScore?: number | null }) {
  const delta = prevScore != null ? roundDelta(report.overall_score - prevScore) : null;
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-6">
        <div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>
              {report.brand} · {report.industry || "—"}
            </span>
            {report.benchmark && (
              <Badge variant="outline" data-testid="geo-benchmark-echo">
                对标标杆:{report.benchmark}
              </Badge>
            )}
          </div>
          <div className="mt-1 flex items-end gap-3">
            <div className="text-5xl font-bold tabular-nums">
              {report.overall_score}
              <span className="text-base font-normal text-muted-foreground"> / 100</span>
            </div>
            {delta != null && delta !== 0 && (
              <span
                data-testid="geo-score-delta"
                className={`mb-1 inline-flex items-center gap-1 text-sm font-semibold ${
                  delta > 0 ? "text-emerald-500" : "text-rose-500"
                }`}
              >
                {delta > 0 ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                {formatScoreDelta(delta)} 较上轮
              </span>
            )}
          </div>
          <ProbeStats report={report} />
          {report.methodology && (
            <div className="mt-2 text-xs text-muted-foreground">{report.methodology}</div>
          )}
        </div>
        <Badge className={`${gradeColor(report.grade)} px-4 py-1 text-lg text-white`}>
          {report.grade}
        </Badge>
      </CardContent>
    </Card>
  );
}
